// ReportTable.tsx
import {
  AssessmentTopicPopup,
  deserify,
  getAssessmentTypeLabel,
  IconWrapper,
  IStyles,
  Loader,
  NoContentCard,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Chip,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { TeacherReportCenterData } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { AssessmentEnum } from '@protos/learning_management/lms.db_pb';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { visuallyHidden } from '@mui/utils';
import { useMemo, useState } from 'react';
import { interactionEvent } from '../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';

type OrderType = 'asc' | 'desc';

const styles: IStyles = {
  root: {},
  tableContainer: {
    border: '1px solid #E8E8E8',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    mt: { xs: pxToRem(25), md: pxTovW(25) },
    mb: { xs: pxToRem(20), md: pxTovW(50) },
  },
  headerRow: {
    backgroundColor: '#E8E8E8',
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(10)}`,
      md: ` ${pxTovW(0)} ${pxTovW(20)}`,
    },
    boxSizing: 'border-box',
  },
  headerCell: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(10)} ${pxTovW(20)} ${pxTovW(0)}`,
      lg: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
  bodyCells: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
};

interface Header {
  name: string;
  cellStyle?: SxProps;
  align?: 'left' | 'center' | 'right';
}

interface ReportTableProps {
  headers: Header[];

  cellSx?: SxProps;
  loading?: string;
  icon?: string;
  genrateReports?: (row: TeacherReportCenterData) => Promise<void>;
}

export const ReportTable = ({
  headers,
  cellSx,
  genrateReports,
  loading,
  icon,
}: ReportTableProps) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  const { reportData } = deserify(useAppSelector((state) => state.report));

  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<OrderType>('asc');
  const [selectedReport, setSelectedReport] = useState<
    TeacherReportCenterData | undefined
  >();
  const [chapterListPopup, setChapterListPopup] = useState(false);

  const handleSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const sortedRows = useMemo(() => {
    if (!reportData?.teacherReports) return [];

    const sorted = [...reportData.teacherReports];

    if (!orderBy) return sorted;

    sorted.sort((a, b) => {
      let aVal: number | string = '';
      let bVal: number | string = '';

      // Sort by timestamp
      if (orderBy === 'assignedDate') {
        aVal = Number(a.assignedDate?.seconds || 0);
        bVal = Number(b.assignedDate?.seconds || 0);
      }
      // Sort by name
      else if (orderBy === 'assessmentName') {
        aVal = a.assessmentName?.toLowerCase() ?? '';
        bVal = b.assessmentName?.toLowerCase() ?? '';
      }
      // Sort by totalSubmissions ratio
      else if (orderBy === 'totalSubmissions') {
        const aTotal = a.totalSubmissions ?? 0;
        const aMax = a.totalAssignments ?? 0;
        const bTotal = b.totalSubmissions ?? 0;
        const bMax = b.totalAssignments ?? 0;

        const aRatio = aMax !== 0 ? aTotal / aMax : 0;
        const bRatio = bMax !== 0 ? bTotal / bMax : 0;

        aVal = aRatio;
        bVal = bRatio;
      }
      // Fallback sort logic
      else {
        const aField = a[orderBy as keyof typeof a];
        const bField = b[orderBy as keyof typeof b];

        aVal =
          typeof aField === 'object' && aField !== null && 'toString' in aField
            ? aField.toString().toLowerCase?.()
            : (aField ?? '').toString().toLowerCase?.();

        bVal =
          typeof bField === 'object' && bField !== null && 'toString' in bField
            ? bField.toString().toLowerCase?.()
            : (bField ?? '').toString().toLowerCase?.();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [reportData?.teacherReports, orderBy, order]);

  function isEmpty(val: any) {
    return (
      val == null || // null or undefined
      (Array.isArray(val) && val.length === 0) ||
      (typeof val === 'object' &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0)
    );
  }

  const ChapterListPopupHandler = (reports: TeacherReportCenterData) => {
    setSelectedReport(reports);
    setChapterListPopup(true);
  };

  return (
    <>
      <TableContainer sx={styles.tableContainer}>
        {loading === 'error' ? (
          <NoContentCard variant="error" icon="error" text="Error Occured" />
        ) : loading === 'loading' ? (
          <Loader />
        ) : sortedRows.length === 0 ? (
          <NoContentCard variant="info" icon="cards" text="No Data available" />
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={styles.headerRow}>
                {headers.map((header, idx) => {
                  const isSortable = [
                    'ASSIGNED DATE',
                    'ASSESSMENT NAME',
                    'TOTAL SUBMISSIONS',
                  ].includes(header.name);
                  const sortKey =
                    header.name === 'ASSIGNED DATE'
                      ? 'assignedDate'
                      : header.name === 'ASSESSMENT NAME'
                      ? 'assessmentName'
                      : header.name === 'TOTAL SUBMISSIONS'
                      ? 'totalSubmissions'
                      : '';

                  return (
                    <TableCell key={idx} align={header.align ?? 'center'}>
                      {isSortable ? (
                        <TableSortLabel
                          active={orderBy === sortKey}
                          direction={orderBy === sortKey ? order : 'asc'}
                          onClick={() => handleSort(sortKey)}
                          hideSortIcon={false}
                          sx={{
                            fontWeight: 'bold',
                            fontSize: pxToRem(14), // or use theme.typography.h3
                            color: '#000',

                            '& .MuiTableSortLabel-icon': {
                              opacity: 1,
                              color:
                                orderBy === sortKey
                                  ? '#000 !important'
                                  : '#8D8D8D !important',
                            },
                          }}
                        >
                          <Typography
                            variant="h3"
                            fontWeight="bold"
                            textAlign="center"
                            noWrap
                          >
                            {header.name}
                          </Typography>
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        </TableSortLabel>
                      ) : (
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          textAlign="center"
                          noWrap
                        >
                          {header.name}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {row.assignedDate?.seconds
                        ? (() => {
                            const timestamp = Number(
                              row.assignedDate.seconds
                                .toString()
                                .replace(/[^\d]/g, '')
                            );
                            const date = new Date(timestamp * 1000);
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              '0'
                            ); // Months are 0-based
                            const year = date.getFullYear();
                            return `${day}-${month}-${year}`;
                          })()
                        : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      textAlign: 'left',
                      maxWidth: pxToRem(100),
                      ...cellSx,
                    }}
                  >
                    <Typography variant="h3" noWrap>
                      {row.assessmentName ?? '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {(row.assessmentType &&
                        getAssessmentTypeLabel(row.assessmentType)) ??
                        '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {row.class ?? '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {row.section ?? '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {row.subject ?? '-'}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: pxToRem(180),
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        // position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        // width: { lg: 'max-content' },
                        // textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          maxWidth: { xs: pxToRem(100), md: pxTovW(200) },
                        }}
                      >
                        {row.moduleName ?? '-'}
                      </Typography>

                      {(row?.assessmentType ===
                        AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT ||
                        row?.assessmentType ===
                          AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT) &&
                        !isEmpty(
                          row.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
                        ) && (
                          <Box
                            sx={{
                              background: 'black',
                              borderRadius: '50%',
                              width: { xs: pxToRem(12) },
                              height: { xs: pxToRem(12) },
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'right',
                              cursor: 'pointer',
                              marginLeft: pxToRem(8),

                              // paddingLeft: pxToRem(8),
                            }}
                            onClick={() => ChapterListPopupHandler(row)}
                          >
                            <Typography
                              variant="smallestText"
                              color="common.white"
                            >
                              i
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Typography variant="h3" noWrap>
                      {row?.totalSubmissions !== undefined &&
                      row?.totalAssignments !== undefined &&
                      row.totalAssignments !== 0
                        ? `${row.totalSubmissions}/${row.totalAssignments}`
                        : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label="View Report"
                        color="primary"
                        size="small"
                        clickable
                        onClick={async () => {
                          row && genrateReports?.(row);
                          await interactionEvent({
                            context: 'Report_Center',
                            name: 'VIEW_REPORT',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <AssessmentTopicPopup
        assessment={selectedReport}
        open={chapterListPopup}
        handleClose={() => setChapterListPopup(false)}
      />
    </>
  );
};
